import { useState, type ReactNode } from 'react'
import { toast } from 'sonner'
import { useDecideInvoiceDuplicate } from '@/features/invoices/data/review-mutations'
import { ActionPopover } from '@/components/action-popover'
import { InlineFieldInput } from '@/components/inline-field-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

type DuplicateOutcome = 'confirmed_duplicate' | 'not_duplicate' | 'superseded'

/**
 * Decide a possible duplicate, opened beside the button that asked for it: the page header's
 * "Resolve duplicate" or the "Possible duplicate" item's "Decide duplicate" (USA-1628).
 */
export function DuplicateDecisionPopover({
  invoiceId,
  trigger,
  align = 'end',
}: {
  invoiceId: string
  trigger: ReactNode
  align?: 'start' | 'end'
}) {
  const decideDuplicate = useDecideInvoiceDuplicate(invoiceId)
  const [outcome, setOutcome] = useState<DuplicateOutcome>('not_duplicate')
  const [replacementInvoiceUuid, setReplacementInvoiceUuid] = useState('')

  const submit = (reason: string, close: () => void) => {
    if (!reason) {
      toast.error('Explain the duplicate decision')
      return
    }
    if (outcome === 'superseded' && !replacementInvoiceUuid.trim()) {
      toast.error('Paste the replacement invoice link')
      return
    }
    const replacementInvoiceId = replacementInvoiceUuid
      .trim()
      .split(/[?#]/, 1)[0]
      ?.split('/')
      .filter(Boolean)
      .at(-1)
    decideDuplicate.mutate(
      {
        outcome,
        reason,
        replacement_invoice_uuid: outcome === 'superseded' ? replacementInvoiceId : undefined,
      },
      {
        onSuccess: () => {
          close()
          setReplacementInvoiceUuid('')
        },
      },
    )
  }

  return (
    <ActionPopover
      align={align}
      trigger={trigger}
      title="Resolve possible duplicate"
      note={{
        label: 'Decision reason',
        placeholder: 'Describe the evidence reviewed',
        minLength: 1,
      }}
      confirmLabel="Save decision"
      confirmVariant={outcome === 'confirmed_duplicate' ? 'destructive' : 'default'}
      pending={decideDuplicate.isPending}
      pendingLabel="Saving..."
      onConfirm={submit}
    >
      <Select value={outcome} onValueChange={(value) => setOutcome(value as DuplicateOutcome)}>
        <SelectTrigger aria-label="Duplicate decision outcome" className="h-8 w-full text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="not_duplicate">Not a duplicate</SelectItem>
          <SelectItem value="confirmed_duplicate">Confirmed duplicate</SelectItem>
          <SelectItem value="superseded">Superseded by revised invoice</SelectItem>
        </SelectContent>
      </Select>
      {outcome === 'superseded' && (
        <InlineFieldInput
          aria-label="Replacement invoice"
          value={replacementInvoiceUuid}
          onChange={(event) => setReplacementInvoiceUuid(event.target.value)}
          placeholder="Paste the replacement invoice link"
          className="h-8 text-sm"
        />
      )}
    </ActionPopover>
  )
}
